import { listConsultations } from "@/core/clinical/clinical-store";
import {
  getAppointmentPractitionerById,
  practitionerDisplayName,
} from "@/core/practitioners/practitioner-store";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";
import { shouldAutoSeedDemoData } from "@/core/demo/is-demo-mode-enabled";
import { apiListLabOrders, apiSyncLabOrder } from "@/core/hospitol-api-client";
import {
  checkLabOrderInsurerGate,
  ensureCoverageCaseForLabOrder,
  linkCoverageCaseToLabOrder,
} from "@/core/insurance/coverage-case-flow";
import { enqueueTicket } from "@/core/queue/service-queue-store";
import { listPatients } from "@/core/patients/patient-store";
import {
  markLabResultReady,
  registerLabOrderForResultsWaiting,
} from "@/core/reception/results-waiting-store";
import { autoBillLabOrderOnComplete } from "@/core/billing/act-completion-billing";
import type { InsurerApprovalStatus, LabOrder, LabOrderInput, LabOrderStatus, LabResultSaveInput } from "./types";
import {
  buildLabResultText,
  normalizeLabResultLines,
} from "./types";

export interface LabOrderCreateResult {
  order: LabOrder;
  insurerPending: boolean;
  coverageCaseId: string | null;
  alertMessage: string | null;
}

export type { LabOrder, LabOrderInput, LabOrderStatus } from "./types";
export {
  COMMON_LAB_TESTS,
  formatLabResultSummary,
  LAB_RESULT_FLAG_LABELS,
  LAB_STATUS_LABELS,
  labOrderConsultationLabel,
  labResultPresetForTest,
} from "./types";

const ORDERS_KEY = "hospitol.labOrders";
const SEQ_KEY = "hospitol.labOrderSeq";
const DEMO_SEED_KEY = "hospitol.lab.demoSeeded";

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = getStorageItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  setStorageItem(key, JSON.stringify(value));
}

function nextOrderNumber(): string {
  const seq = Number(getStorageItem(SEQ_KEY) ?? "0") + 1;
  setStorageItem(SEQ_KEY, String(seq));
  return `LAB-${String(seq).padStart(5, "0")}`;
}

function sortByCreatedDesc(a: LabOrder, b: LabOrder): number {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

function resultLineId(): string {
  return `lrl-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function normalizeLabOrder(raw: Record<string, unknown>): LabOrder {
  const consultationId = raw.consultationId ? String(raw.consultationId) : null;
  const resultLines = normalizeLabResultLines(raw.resultLines);
  const resultText = String(raw.resultText ?? "");
  return {
    id: String(raw.id ?? ""),
    orderNumber: String(raw.orderNumber ?? ""),
    patientId: String(raw.patientId ?? ""),
    consultationId,
    prescriberId: String(raw.prescriberId ?? ""),
    prescriber: String(raw.prescriber ?? ""),
    testName: String(raw.testName ?? ""),
    clinicalNotes: String(raw.clinicalNotes ?? ""),
    status: (raw.status as LabOrderStatus) ?? "requested",
    insurerApproval: (raw.insurerApproval as InsurerApprovalStatus) ?? "none",
    coverageCaseId: raw.coverageCaseId ? String(raw.coverageCaseId) : null,
    resultLines,
    resultText:
      resultText ||
      (resultLines.length > 0 ? buildLabResultText(resultLines) : ""),
    resultAt: raw.resultAt ? String(raw.resultAt) : null,
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? new Date().toISOString()),
  };
}

function readLabOrders(): LabOrder[] {
  return readJson<Record<string, unknown>[]>(ORDERS_KEY, []).map(normalizeLabOrder);
}

function replaceLabOrdersCache(orders: LabOrder[]): void {
  writeJson(ORDERS_KEY, orders);
}

function replicateLabOrderToApi(order: LabOrder): void {
  void apiSyncLabOrder(order);
}

export class LabOrderValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "LabOrderValidationError";
  }
}

export function ensureDemoLabOrders() {
  if (!shouldAutoSeedDemoData()) return;
  if (getStorageItem(DEMO_SEED_KEY)) return;

  const patient = listPatients()[0];
  if (!patient) return;

  const consultation = listConsultations().find((c) => c.patientId === patient.id);

  const order = createLabOrder({
    patientId: patient.id,
    prescriberId: "demo-prac-mukendi",
    consultationId: consultation?.id ?? null,
    testName: "NFS (Numération formule sanguine)",
    clinicalNotes: "Fièvre persistante — contrôle post-consultation",
  }).order;
  updateLabOrderStatus(order.id, "in_progress");

  setStorageItem(DEMO_SEED_KEY, new Date().toISOString());
}

export function listLabOrders(): LabOrder[] {
  ensureDemoLabOrders();
  return readLabOrders().sort(sortByCreatedDesc);
}

/** Télécharge les demandes labo depuis l'API et met à jour le cache local. */
export async function syncLabOrdersFromApi(): Promise<boolean> {
  const remote = await apiListLabOrders();
  if (!remote) return false;
  if (remote.length > 0) {
    replaceLabOrdersCache(remote);
    return true;
  }
  const local = readLabOrders();
  if (local.length > 0) {
    for (const order of local) {
      replicateLabOrderToApi(order);
    }
    return true;
  }
  return true;
}

export function listLabOrdersByStatus(status: LabOrderStatus): LabOrder[] {
  return listLabOrders().filter((o) => o.status === status);
}

export function listLabOrdersByPatient(patientId: string): LabOrder[] {
  return listLabOrders().filter((o) => o.patientId === patientId);
}

export function listLabOrdersByConsultation(consultationId: string): LabOrder[] {
  return listLabOrders().filter((o) => o.consultationId === consultationId);
}

export function getLabOrderById(id: string): LabOrder | null {
  return listLabOrders().find((o) => o.id === id) ?? null;
}

export function createLabOrder(input: LabOrderInput): LabOrderCreateResult {
  if (!input.patientId?.trim()) {
    throw new LabOrderValidationError("Patient requis.");
  }
  if (!input.prescriberId?.trim()) {
    throw new LabOrderValidationError("Prescripteur requis.");
  }
  if (!input.testName?.trim()) {
    throw new LabOrderValidationError("Type d'analyse requis.");
  }

  const gate = checkLabOrderInsurerGate({
    patientId: input.patientId,
    testName: input.testName,
    consultationId: input.consultationId,
  });

  let insurerApproval: InsurerApprovalStatus = "none";
  let status: LabOrderStatus = "requested";
  let coverageCaseId: string | null = null;
  let alertMessage: string | null = null;

  if (gate.validationRequired && !gate.allowed) {
    insurerApproval = "pending";
    status = "pending_insurer";
    alertMessage =
      `Demande enregistrée — en attente de validation assureur pour « ${input.testName} ». ` +
      "Le laboratoire peut préparer le prélèvement. Le patient peut aussi payer une proforma en attendant.";
  } else if (gate.validationRequired && gate.allowed) {
    insurerApproval = "approved";
  }

  const practitioner = getAppointmentPractitionerById(input.prescriberId);
  const prescriberName =
    input.prescriber?.trim() ||
    practitioner?.displayName ||
    practitionerDisplayName(input.prescriberId, "");

  const now = new Date().toISOString();
  const order: LabOrder = {
    id: `lab-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    orderNumber: nextOrderNumber(),
    patientId: input.patientId.trim(),
    consultationId: input.consultationId?.trim() || null,
    prescriberId: input.prescriberId.trim(),
    prescriber: prescriberName,
    testName: input.testName.trim(),
    clinicalNotes: input.clinicalNotes?.trim() ?? "",
    status,
    insurerApproval,
    coverageCaseId: null,
    resultLines: [],
    resultText: "",
    resultAt: null,
    createdAt: now,
    updatedAt: now,
  };
  const list = readLabOrders();
  list.unshift(order);
  writeJson(ORDERS_KEY, list);

  if (insurerApproval === "pending") {
    const coverageCase = ensureCoverageCaseForLabOrder({
      patientId: order.patientId,
      testName: order.testName,
      consultationId: order.consultationId,
      labOrderId: order.id,
    });
    if (coverageCase) {
      coverageCaseId = coverageCase.id;
      linkCoverageCaseToLabOrder(coverageCase.id, order.id);
      order.coverageCaseId = coverageCaseId;
      list[0] = order;
      writeJson(ORDERS_KEY, list);
    }
  }

  enqueueTicket({
    patientId: order.patientId,
    serviceId: "laboratory",
    unitName: "Laboratoire",
    motif: order.testName,
  });

  registerLabOrderForResultsWaiting({
    patientId: order.patientId,
    labOrderId: order.id,
    orderNumber: order.orderNumber,
    testName: order.testName,
  });

  replicateLabOrderToApi(order);

  return { order, insurerPending: insurerApproval === "pending", coverageCaseId, alertMessage };
}

export function updateLabOrderStatus(id: string, status: LabOrderStatus): LabOrder | null {
  const list = readLabOrders();
  const index = list.findIndex((o) => o.id === id);
  if (index < 0) return null;

  const now = new Date().toISOString();
  const updated: LabOrder = {
    ...list[index]!,
    status,
    updatedAt: now,
    resultAt: status === "completed" ? list[index]!.resultAt ?? now : list[index]!.resultAt,
  };
  list[index] = updated;
  writeJson(ORDERS_KEY, list);
  replicateLabOrderToApi(updated);
  return updated;
}

export function patchLabOrderInsurerApproval(
  id: string,
  insurerApproval: InsurerApprovalStatus,
): LabOrder | null {
  const list = readLabOrders();
  const index = list.findIndex((o) => o.id === id);
  if (index < 0) return null;

  const now = new Date().toISOString();
  let status = list[index]!.status;
  if (status === "pending_insurer" && insurerApproval === "approved") {
    status = "requested";
  }
  if (insurerApproval === "rejected") {
    status = list[index]!.status === "pending_insurer" ? "requested" : list[index]!.status;
  }

  const updated: LabOrder = {
    ...list[index]!,
    insurerApproval,
    status,
    updatedAt: now,
  };
  list[index] = updated;
  writeJson(ORDERS_KEY, list);
  replicateLabOrderToApi(updated);
  return updated;
}

export function saveLabResult(id: string, input: LabResultSaveInput | string): LabOrder | null {
  const list = readLabOrders();
  const index = list.findIndex((o) => o.id === id);
  if (index < 0) return null;

  const now = new Date().toISOString();
  let resultLines: LabResultLine[] = [];
  let resultText = "";

  if (typeof input === "string") {
    resultText = input.trim();
  } else {
    resultLines = input.resultLines
      .filter((l) => l.parameter.trim() || l.value.trim())
      .map((line) => ({
        id: resultLineId(),
        parameter: line.parameter.trim(),
        value: line.value.trim(),
        unit: line.unit.trim(),
        referenceRange: line.referenceRange.trim(),
        flag: line.flag ?? "",
      }));
    const comment = input.resultText?.trim() ?? "";
    resultText = buildLabResultText(resultLines, comment);
    if (!resultText.trim() && resultLines.length === 0) {
      throw new LabOrderValidationError("Saisissez au moins une ligne de résultat.");
    }
  }

  if (!resultText.trim()) {
    throw new LabOrderValidationError("Résultat requis.");
  }

  const updated: LabOrder = {
    ...list[index]!,
    resultLines,
    resultText,
    status: "completed",
    resultAt: now,
    updatedAt: now,
  };
  list[index] = updated;
  writeJson(ORDERS_KEY, list);
  markLabResultReady(id);
  replicateLabOrderToApi(updated);
  void autoBillLabOrderOnComplete(updated);
  return updated;
}

export function countPendingLabOrders(): number {
  return listLabOrders().filter((o) => o.status === "requested" || o.status === "in_progress").length;
}

export function countLabAwaitingResult(): number {
  return listLabOrders().filter((o) => o.status === "in_progress").length;
}
